import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "../brand/Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { scrollToId } from "../../lib/scroll";

export const NAV_LINKS = [
  ["inicio", "Inicio"],
  ["como-funciona", "Cómo funciona"],
  ["soluciones", "Nuestras soluciones"],
  ["oportunidad", "Oportunidad"],
  ["membresia", "Membresía Dorada"],
  ["faq", "Preguntas frecuentes"],
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="container-x flex h-[72px] items-center justify-between gap-3">
          <button onClick={() => go("inicio")} data-testid="nav-logo" aria-label="Ir al inicio" className="shrink-0">
            <Logo tone="light" className="h-7 sm:h-9" />
          </button>
          <nav className="hidden items-center gap-4 min-[1500px]:flex" aria-label="Principal">
            {NAV_LINKS.map(([id, label]) => (
              <button
                key={id}
                onClick={() => go(id)}
                data-testid={`nav-link-${id}`}
                className="group relative whitespace-nowrap text-xs font-semibold uppercase tracking-[0.03em] text-white/80 transition-colors duration-300 hover:text-white"
              >
                {label}
                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-leaf transition-transform duration-500 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <Link
              to="/login"
              data-testid="nav-ingresar"
              className="hidden whitespace-nowrap rounded-full px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.03em] text-white/85 transition-colors hover:text-leaf sm:inline-flex"
            >
              Ingresar
            </Link>
            <Link
              to="/registro"
              data-testid="nav-registrarme"
              className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-leaf px-3.5 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.04em] text-ink transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#9bd94b] sm:px-5 sm:text-sm"
            >
              Registrarme <ArrowUpRight className="btn-arrow hidden h-4 w-4 sm:block" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-mobile-open"
              aria-label="Abrir menú"
              className="grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white min-[1500px]:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && <MobileMenu onClose={() => setOpen(false)} go={go} />}
      </AnimatePresence>
    </>
  );
};

const MobileMenu = ({ onClose, go }) => (
  <motion.div
    data-testid="nav-mobile-menu"
    className="fixed inset-0 z-[60] flex flex-col bg-ink text-white"
    initial={{ clipPath: "circle(0% at 92% 4%)" }}
    animate={{ clipPath: "circle(150% at 92% 4%)" }}
    exit={{ clipPath: "circle(0% at 92% 4%)" }}
    transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
  >
    <div className="container-x flex h-[72px] items-center justify-between">
      <Logo tone="light" className="h-8" testId="mobile-menu-logo" />
      <button onClick={onClose} data-testid="nav-mobile-close" aria-label="Cerrar menú" className="grid h-11 w-11 place-items-center rounded-full border border-white/20">
        <X className="h-5 w-5" />
      </button>
    </div>
    <nav className="container-x mt-6 flex flex-1 flex-col gap-1 overflow-y-auto pb-10">
      {NAV_LINKS.map(([id, label], i) => (
        <motion.button
          key={id}
          onClick={() => go(id)}
          data-testid={`nav-mobile-link-${id}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 + i * 0.05, duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="flex items-baseline gap-4 border-b border-white/10 py-4 text-left font-display text-3xl font-semibold tracking-tight sm:text-4xl"
        >
          <span className="font-mono text-xs text-leaf">0{i + 1}</span>
          {label}
        </motion.button>
      ))}
      <Link to="/registro" data-testid="nav-mobile-registrarme" className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-leaf px-6 py-3 font-display text-sm font-semibold uppercase tracking-[0.04em] text-ink">
        Registrarme
      </Link>
      <Link to="/login" data-testid="nav-mobile-ingresar" className="mt-5 font-mono text-sm uppercase tracking-[0.12em] text-white/80">
        Ingresar →
      </Link>
    </nav>
  </motion.div>
);
